import {
    ILibraryDocumentListItem,
    ILibraryFolder,
    ILibraryListComparison,
    ILibraryListItem,
    ILibraryListScope,
    IScope,
    ScopeStatus,
} from "@/api";
import { CompareInChartButton } from "@/components/frontend/dialogs/create-chart/CompareInChartButton";
import { MoveToFolderModal } from "@/components/shared/move-to-folder";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { FolderInput, MessageSquare, Pencil, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";

import { RenameDocumentDialog } from "@/components/frontend/documents/RenameDocumentDialog";

import {
    isClientRootFolder,
    isForeignSharedSubject,
    isProtectedRootListItem,
    isSharedRootFolder,
} from "../utils/folderTreeUtils";

import { BulkDeleteDialog } from "./BulkDeleteDialog";
import { EditFolderDialog } from "./EditFolderDialog";
import { RenameComparisonDialog } from "./RenameComparisonDialog";
import { RenameScopeDialog } from "./RenameScopeDialog";

const FOREIGN_SHARED_REASON =
    "Items shared with your group can't be renamed, moved or deleted";
const FOREIGN_SHARED_POLLY_REASON =
    "Items shared with your group can't be attached to Polly";
const SHARED_ROOT_REASON =
    "The Shared folder can't be renamed, moved or deleted";
const CLIENT_ROOT_REASON =
    "A client's root folder can't be renamed, moved or deleted";
const PROTECTED_ROOTS_REASON =
    "These folders can't be renamed, moved or deleted";
const PROTECTED_ITEMS_REASON = "These items can't be renamed, moved or deleted";
const UNAVAILABLE_ACTION_CLASS =
    "aria-disabled:cursor-not-allowed aria-disabled:opacity-50";

const unavailableReasonFor = (items: ILibraryListItem[]) => {
    if (items.every((i) => isForeignSharedSubject(i)))
        return FOREIGN_SHARED_REASON;
    if (items.every((i) => isSharedRootFolder(i))) return SHARED_ROOT_REASON;
    if (items.every((i) => isClientRootFolder(i))) return CLIENT_ROOT_REASON;
    return items.every((i) => i.type === "folder")
        ? PROTECTED_ROOTS_REASON
        : PROTECTED_ITEMS_REASON;
};

const leftOutNotice = (
    kept: number,
    total: number,
    verb: string,
    reason: string
) => `${kept} of ${total} will be ${verb}. ${reason}.`;

interface ActionsBarProps {
    selectedItems: ILibraryListItem[];
    onClearSelection: () => void;
    onAction: () => void;
    onAskPolly: (items: ILibraryListItem[]) => void;
    mutationsUnavailableReason?: string | null;
}

export const ActionsBar = ({
    selectedItems,
    onClearSelection,
    onAction,
    onAskPolly,
    mutationsUnavailableReason = null,
}: ActionsBarProps) => {
    const queryClient = useQueryClient();

    const [renameOpen, setRenameOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const count = selectedItems.length;
    const allScopes =
        count > 0 && selectedItems.every((i) => i.type === "scope");

    const mutationItems = useMemo(
        () =>
            mutationsUnavailableReason
                ? []
                : selectedItems.filter((i) => !isProtectedRootListItem(i)),
        [selectedItems, mutationsUnavailableReason]
    );
    const nothingToMutate = count > 0 && mutationItems.length === 0;
    const pollyItems = useMemo(
        () =>
            mutationsUnavailableReason
                ? []
                : selectedItems.filter((i) => !isForeignSharedSubject(i)),
        [selectedItems, mutationsUnavailableReason]
    );
    const nothingForPolly = count > 0 && pollyItems.length === 0;
    const leftOutOfMutation = useMemo(
        () =>
            mutationItems.length > 0
                ? selectedItems.filter(isProtectedRootListItem)
                : [],
        [selectedItems, mutationItems]
    );
    const mutationNotice = (verb: string) =>
        leftOutOfMutation.length > 0
            ? leftOutNotice(
                  mutationItems.length,
                  count,
                  verb,
                  unavailableReasonFor(leftOutOfMutation)
              )
            : null;
    const moveNotice = mutationNotice("moved");
    const deleteNotice = mutationNotice("deleted");
    const pollyNotice =
        pollyItems.length > 0 && pollyItems.length < count
            ? leftOutNotice(
                  pollyItems.length,
                  count,
                  "attached",
                  FOREIGN_SHARED_POLLY_REASON
              )
            : null;
    const onlyForeignItems =
        count > 0 && selectedItems.every(isForeignSharedSubject);
    const unavailableReason = onlyForeignItems
        ? unavailableReasonFor(selectedItems)
        : (mutationsUnavailableReason ?? unavailableReasonFor(selectedItems));

    const exactlyOne = mutationItems.length === 1;
    const singleItem = exactlyOne ? mutationItems[0] : null;
    const labelSubject = singleItem ?? (count === 1 ? selectedItems[0] : null);
    const singleFolder = labelSubject?.type === "folder";

    const handleSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ["frontend-library-list"] });
        queryClient.invalidateQueries({
            queryKey: ["frontend-library-folders"],
        });
        queryClient.invalidateQueries({ queryKey: ["library-documents"] });
        onAction();
    };

    const handleRenameSuccess = () => {
        setRenameOpen(false);
        handleSuccess();
    };

    const handleMoveSuccess = () => {
        onClearSelection();
        handleSuccess();
    };

    const handleDeleteSuccess = () => {
        onClearSelection();
        handleSuccess();
    };

    const scopesWithChecklist = useMemo(
        () =>
            allScopes
                ? selectedItems.filter((i) => i.clause_checklist_id != null)
                : [],
        [selectedItems, allScopes]
    );

    const selectedScopeIds = useMemo(
        () => scopesWithChecklist.map((i) => i.id),
        [scopesWithChecklist]
    );

    const selectedScopeRows = useMemo(
        (): Pick<IScope, "id" | "clause_checklist_id" | "document_title">[] =>
            scopesWithChecklist.map((i) => ({
                id: i.id,
                clause_checklist_id: i.clause_checklist_id!,
                document_title: i.name,
            })),
        [scopesWithChecklist]
    );

    const clauseChecklistName =
        scopesWithChecklist[0]?.clause_checklist_name ?? "";

    const renameDialog = (() => {
        if (!singleItem) return null;
        switch (singleItem.type) {
            case "document": {
                const doc: ILibraryDocumentListItem = {
                    id: singleItem.id,
                    document_name: singleItem.name,
                    library_folder_id: singleItem.library_folder_id,
                    industry_id: null,
                    carrier_id: null,
                    client_id: singleItem.client_id,
                    industry_name: singleItem.industry_name,
                    carrier_name: singleItem.carrier_name,
                    client_name: singleItem.client_name,
                    organization_name: null,
                    textract_status: singleItem.status,
                    page_count: null,
                    file_size_bytes: null,
                    created_at: singleItem.created_at,
                };
                return (
                    <RenameDocumentDialog
                        open={renameOpen}
                        onClose={() => setRenameOpen(false)}
                        document={doc}
                        onRenamed={handleRenameSuccess}
                    />
                );
            }
            case "scope": {
                const scope: ILibraryListScope = {
                    type: "scope",
                    id: singleItem.id,
                    document_title: singleItem.name,
                    status:
                        (singleItem.status as ScopeStatus) ??
                        ScopeStatus.PENDING,
                    client_name: singleItem.client_name,
                    clause_checklist_id: singleItem.clause_checklist_id,
                    clause_checklist_name: singleItem.clause_checklist_name,
                    library_folder_id: singleItem.library_folder_id,
                    created_at: singleItem.created_at,
                };
                return (
                    <RenameScopeDialog
                        open={renameOpen}
                        onClose={() => setRenameOpen(false)}
                        scope={scope}
                        onRenamed={handleRenameSuccess}
                    />
                );
            }
            case "comparison": {
                const comp: ILibraryListComparison = {
                    type: "comparison",
                    id: singleItem.id,
                    name: singleItem.name,
                    client_name: singleItem.client_name,
                    clause_checklist_name: singleItem.clause_checklist_name,
                    scope_count: 0,
                    library_folder_id: singleItem.library_folder_id,
                    created_at: singleItem.created_at,
                };
                return (
                    <RenameComparisonDialog
                        open={renameOpen}
                        onClose={() => setRenameOpen(false)}
                        comparison={comp}
                        onRenamed={handleRenameSuccess}
                    />
                );
            }
            case "folder": {
                const folder = {
                    id: singleItem.id,
                    name: singleItem.name,
                    is_knowledge_base: singleItem.is_knowledge_base ?? false,
                    client_id: singleItem.client_id,
                    client:
                        singleItem.client_id && singleItem.client_name
                            ? {
                                  id: singleItem.client_id,
                                  name: singleItem.client_name,
                              }
                            : null,
                    parent_id: singleItem.parent_id,
                    organization_id: 0,
                    created_at: singleItem.created_at,
                    updated_at: singleItem.updated_at ?? singleItem.created_at,
                } as ILibraryFolder;
                return (
                    <EditFolderDialog
                        open={renameOpen}
                        onClose={() => setRenameOpen(false)}
                        folder={folder}
                        onSaved={handleRenameSuccess}
                    />
                );
            }
        }
    })();

    return (
        <>
            <div
                data-slot="actions-bar"
                className={cn(
                    "mb-2 flex items-center gap-2 rounded-lg border px-3 py-1.5",
                    "border-primary/[0.12] bg-primary/[0.04]"
                )}
            >
                <span className="text-primary mr-1 text-xs font-semibold whitespace-nowrap">
                    {count} selected
                </span>

                <Separator orientation="vertical" className="h-5" />

                {nothingForPolly ? (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger
                                render={
                                    <Button
                                        aria-disabled
                                        className={UNAVAILABLE_ACTION_CLASS}
                                    />
                                }
                            >
                                <MessageSquare size={14} className="mr-1.5" />
                                Ask Polly
                            </TooltipTrigger>
                            <TooltipContent>
                                {onlyForeignItems
                                    ? FOREIGN_SHARED_POLLY_REASON
                                    : (mutationsUnavailableReason ??
                                      FOREIGN_SHARED_POLLY_REASON)}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : pollyNotice ? (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger
                                render={
                                    <Button
                                        onClick={() => onAskPolly(pollyItems)}
                                    />
                                }
                            >
                                <MessageSquare size={14} className="mr-1.5" />
                                Ask Polly
                            </TooltipTrigger>
                            <TooltipContent>{pollyNotice}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : (
                    <Button
                        onClick={() => onAskPolly(pollyItems)}
                        disabled={count === 0}
                    >
                        <MessageSquare size={14} className="mr-1.5" />
                        Ask Polly
                    </Button>
                )}

                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger
                            render={
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    aria-disabled={!exactlyOne}
                                    className={UNAVAILABLE_ACTION_CLASS}
                                    onClick={() => {
                                        if (exactlyOne) setRenameOpen(true);
                                    }}
                                />
                            }
                        >
                            <Pencil size={14} className="mr-1.5" />
                            {singleFolder ? "Edit" : "Rename"}
                        </TooltipTrigger>
                        {nothingToMutate ? (
                            <TooltipContent>{unavailableReason}</TooltipContent>
                        ) : (
                            !exactlyOne && (
                                <TooltipContent>
                                    Select exactly one item to rename
                                </TooltipContent>
                            )
                        )}
                    </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger
                            render={
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    aria-disabled={nothingToMutate}
                                    className={UNAVAILABLE_ACTION_CLASS}
                                    onClick={() => {
                                        if (!nothingToMutate) setMoveOpen(true);
                                    }}
                                />
                            }
                        >
                            <FolderInput size={14} className="mr-1.5" />
                            Move
                        </TooltipTrigger>
                        {nothingToMutate ? (
                            <TooltipContent>{unavailableReason}</TooltipContent>
                        ) : (
                            moveNotice && (
                                <TooltipContent>{moveNotice}</TooltipContent>
                            )
                        )}
                    </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger
                            render={
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={cn(
                                        "text-destructive hover:bg-destructive/10",
                                        UNAVAILABLE_ACTION_CLASS
                                    )}
                                    aria-disabled={nothingToMutate}
                                    onClick={() => {
                                        if (!nothingToMutate)
                                            setDeleteOpen(true);
                                    }}
                                />
                            }
                        >
                            <Trash2 size={14} className="mr-1.5" />
                            Delete
                        </TooltipTrigger>
                        {nothingToMutate ? (
                            <TooltipContent>{unavailableReason}</TooltipContent>
                        ) : (
                            deleteNotice && (
                                <TooltipContent>{deleteNotice}</TooltipContent>
                            )
                        )}
                    </Tooltip>
                </TooltipProvider>

                {allScopes && count > 0 && (
                    <CompareInChartButton
                        selectedScopeIds={selectedScopeIds}
                        rows={selectedScopeRows}
                        clauseChecklistName={clauseChecklistName}
                        tooltipPlacement="bottom"
                        size="small"
                        variant="outlined"
                    />
                )}

                <Button
                    variant="ghost"
                    size="icon-sm"
                    className="ml-auto"
                    onClick={onClearSelection}
                    aria-label="Clear selection"
                >
                    <X size={16} />
                </Button>
            </div>

            {renameDialog}

            <MoveToFolderModal
                open={moveOpen}
                onClose={() => setMoveOpen(false)}
                items={mutationItems}
                excludedItems={leftOutOfMutation}
                onMoved={handleMoveSuccess}
            />

            <BulkDeleteDialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                items={mutationItems}
                onDeleted={handleDeleteSuccess}
            />
        </>
    );
};
